<template>
  <feature-card-basic
    ref="card"
    @update:feature="featureFetched"
    :title="title"
    :layer="layer"
    hide-feature-watcher
    hide-object-extras
  >
    <template #buttons><span></span></template>
    <template #toolsCustomButtons>
      <base-rounded-button
        icon="mdi-car-connected"
        size="x-small"
        color="rgba(235, 235, 235, 0.5)"
        iconColor="#202124"
        @click="openProviderApp"
        :tooltip="$i18n.t('button.openVehiclesProvider')"
      />
    </template>
    <template #top v-if="vehiclesHistoryVisible">
      <v-row no-gutters>
        <v-col cols="12" class="pr-5">
          <v-row no-gutters class="pt-3 pb-2 justify-start">
            <v-col cols="auto" class="pl-1">
              <card-title-row
                is-subtitle
                translation-path="sidebar.locationHistory"
                :has-buttons="false"
                class="pt-5 pb-4 text-left"
              >
                <template #subtitle>
                  <italic-subheader translation-path="sidebar.locationHistorySubtitle" />
                </template>
              </card-title-row>
            </v-col>
          </v-row>
          <sidebar-row>
            <v-row no-gutters class="justify-start" align="center">
              <v-col cols="5">
                <data-input v-model="dates.startDate" v-bind="dateBind" @saveDate="checkDates" />
              </v-col>
              <v-col cols="auto" class="px-2">
                -
              </v-col>
              <v-col cols="5">
                <data-input v-model="dates.endDate" v-bind="dateBind" @saveDate="checkDates" />
              </v-col>
            </v-row>
          </sidebar-row>
          <sidebar-row>
            <v-row no-gutters class="pt-3 pb-4 justify-start" :class="{ border: !historyResultVisible }">
              <base-button
                class="mr-0"
                translation-path="button.show"
                :disabled="!datesValid"
                :loading="historyLoading"
                @click="showHistory"
              />
            </v-row>
          </sidebar-row>
          <sidebar-row v-if="historyResultVisible">
            <v-row no-gutters class="justify-start border" align="center">
              <v-col cols="1">
                <data-input :data-type="{ name: 'color' }" v-model="historyColor" @input="updateStyle" />
              </v-col>
              <v-col cols="10" class="px-2 pb-1 text-body-2">
                {{ `${historyResult.deviceName}: ${historyResult.date}` }}
              </v-col>
              <v-spacer />
              <v-col cols="1" class="pb-1">
                <dots-menu :items="dotsMenuItems" @delete="deleteHistoryResult" @shp="downloadHistoryResultShp" />
              </v-col>
            </v-row>
          </sidebar-row>
        </v-col>
      </v-row>
    </template>
  </feature-card-basic>
</template>

<script>
import { call, get } from 'vuex-pathify';
import { debounce } from 'lodash';

import { Icon, Stroke, Style } from 'ol/style';
import { Point } from 'ol/geom';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import FeatureCardBasic from '@/components/FeatureCardBasic';
import ItalicSubheader from '@/components/ItalicSubheader';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'FeatureCardVehicle',
  components: {
    CardTitleRow,
    DataInput,
    DotsMenu,
    FeatureCardBasic,
    ItalicSubheader,
    SidebarRow,
  },
  props: {
    layer: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    feature: undefined,
    dates: {},
    historyColor: '',
    historyResult: undefined,
    historyLoading: false,
    shpLoading: false,
  }),
  computed: {
    modulesPermissions: get('users/toolsPermissions'),
    vehiclesHistoryModuleEnabled: get('admin/additionalModules@VEHICLES_HISTORY.enabled'),
    vehiclesHistorySettingEnabled: get('admin/settingsValues@vehicles_module_location_history_module_enabled'),
    vehiclesHistoryColor: get('admin/settingsValues@vehicles_history_localization_color'),
    vehiclesEngineStatusAttribute: get('admin/modulesMapping@vehicles.attributes.engine_status'),
    vehiclesSpeedAttribute: get('admin/modulesMapping@vehicles.attributes.vehicle_speed'),
    vehiclesProviderUrl: get('admin/modulesMapping@vehicles.provider_url'),
    interval: get('admin/settingsValues@vehicles_module_locations_refresh_interval'),
    zoom: get('map/zoom'),
    vehiclesHistoryVisible() {
      return this.vehiclesHistoryModuleEnabled && this.vehiclesHistorySettingEnabled && this.permission;
    },
    routeParams() {
      return this.$route.params;
    },
    title() {
      if (!this.feature) {
        return;
      }
      return this.feature.properties.deviceName;
    },
    fid() {
      return this.$route.params.fid;
    },
    dotsMenuItems() {
      return [
        {
          name: 'shp',
          disabled: this.shpLoading,
        },
        {
          name: 'delete',
          disabled: this.shpLoading,
        },
      ];
    },
    dateBind() {
      return {
        dataType: { name: 'datetime' },
        hideDetails: true,
        prependInnerIcon: 'mdi-calendar',
        disabled: this.historyLoading,
      };
    },
    datesValid() {
      const { startDate, endDate } = this.dates;
      if (startDate && endDate) {
        const date1 = this.$dayjs(startDate);
        const date2 = this.$dayjs(endDate);
        return date1.isBefore(date2) || startDate === endDate;
      }
      return false;
    },
    historyResultVisible() {
      return this.historyResult && !this.historyLoading;
    },
    permission() {
      return this.modulesPermissions['VEHICLES_HISTORY'].main_value;
    },
  },
  watch: {
    fid() {
      clearTimeout(this.timeoutId);
    },
  },
  methods: {
    getVehicleHistory: call('layers/getVehicleHistory'),
    getVehicleHistoryShp: call('layers/getVehicleHistoryShp'),
    checkDates(e, dates = this.dates) {
      const { startDate, endDate } = JSON.parse(JSON.stringify(dates));
      if (startDate && !endDate) {
        const endDateEnd = `${startDate.split('T')[0]}T23:59:59.000`;
        this.dates = {
          startDate: startDate,
          endDate: endDateEnd,
        };
      } else if (!startDate) {
        return;
      }
      const date1 = this.$dayjs(startDate);
      const date2 = this.$dayjs(endDate);
      if (date2.isBefore(date1)) {
        this.dates = {
          startDate: endDate,
          endDate: startDate,
        };
      }
    },
    deleteHistoryResult() {
      this.historyResult = undefined;
      this.historyColor = this.vehiclesHistoryColor;
      this.$root.$emit('clearVisibleGeomLayerAndFitView', 'VehicleHistory');
    },
    getVehicleHistoryStyle(feature) {
      const color = this.historyColor;
      const geometry = feature.getGeometry();
      const styles = [
        new Style({
          stroke: new Stroke({
            color,
            width: 2,
          }),
        }),
      ];
      geometry.forEachSegment((start, end) => {
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const midPoint = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
        const rotation = Math.atan2(dy, dx);
        let scale = 0.85;
        if (this.zoom < 15) {
          scale = 0.55;
        } else if (this.zoom < 18) {
          scale = 0.7;
        }
        styles.push(
          new Style({
            geometry: new Point(midPoint),
            image: new Icon({
              src: require('@/assets/icons/arrow.svg'),
              color,
              anchor: [0.75, 0.5],
              rotateWithView: true,
              rotation: -rotation,
              scale,
            }),
          })
        );
      });
      return styles;
    },
    updateStyle() {
      this.$root.$emit('setLayerStyle', 'VehicleHistory', this.getVehicleHistoryStyle);
    },
    async downloadHistoryResultShp() {
      this.shpLoading = true;
      try {
        const payload = {
          featureId: this.fid,
          payload: {
            date_since: this.historyResult.startDate,
            date_until: this.historyResult.endDate,
          },
        };
        await this.getVehicleHistoryShp(payload);
        this.shpLoading = false;
      } catch {
        this.shpLoading = false;
      }
    },
    async showHistory() {
      try {
        this.historyLoading = true;
        const formattedStartDate = this.$dayjs(this.dates.startDate).format('L');
        const formattedEndDate = this.$dayjs(this.dates.endDate).format('L');
        this.historyResult = {
          deviceName: this.feature.properties.deviceName,
          date: `${formattedStartDate} - ${formattedEndDate}`,
          startDate: this.dates.startDate,
          endDate: this.dates.endDate,
        };
        const payload = {
          featureId: this.fid,
          payload: {
            date_since: this.dates.startDate,
            date_until: this.dates.endDate,
          },
        };
        const r = await this.getVehicleHistory(payload);
        if (r.length < 1) {
          this.deleteHistoryResult();
          this.historyLoading = false;
          this.$store.set('snackbar/PUSH_ERROR!', {
            message: this.$i18n.t('errors.noDataToDisplay'),
          });
          return;
        }
        const coordinates = r.map(coord => coord.coordinate.coordinates);
        const geometry = {
          type: 'LineString',
          coordinates,
          crs: {
            properties: {
              name: 'EPSG:4326',
            },
            type: 'name',
          },
        };
        const layerParams = {
          style: feature => this.getVehicleHistoryStyle(feature),
        };
        this.$root.$emit('createVisibleGeomLayerAndFitView', geometry, 'VehicleHistory', {}, layerParams);
        this.historyLoading = false;
      } catch {
        this.historyResult = undefined;
        this.historyLoading = false;
      }
    },
    openProviderApp() {
      window.open(this.vehiclesProviderUrl, '_blank');
    },
    updateFeature() {
      this.timeoutId = setTimeout(() => {
        this.$refs.card.getFeature();
      }, (this.interval || 5) * 1000);
    },
    featureFetched($event) {
      this.feature = $event;
      this.updateFeature();
    },
  },
  mounted() {
    this.$on('hook:beforeDestroy', () => {
      clearTimeout(this.timeoutId);
    });
  },
  created() {
    this.checkDates = debounce(this.checkDates, 100);
    this.updateStyle = debounce(this.updateStyle, 500);
    this.historyColor = this.vehiclesHistoryColor;
  },
  beforeDestroy() {
    this.deleteHistoryResult();
  },
};
</script>

<style lang="scss" scoped>
.border {
  @include border(bottom);
}
</style>
